/*
 * File: CommentDiscardConfirmationModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2023 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 28, 2025 at 3:54 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

// Components
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Typography } from '../Typography';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './CommentDiscardConfirmationModal.styles';


/**
 * Component
 */

export const CommentDiscardConfirmationModal = memo(({
	className,
	isOpen,
	handleCancel,
	handleConfirm
}) => {

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<Modal className={className} isOpen={isOpen} clickOutsideClose={false} showClose={false} useWrapper>
			<S.ModalInner>

				{/* Header */}
				<S.Header>

					{/* Prompt */}
					<Typography tag="h4">
						<Trans t={t} i18nKey="modals.modalInquiry">
							Just Checking...
						</Trans>
					</Typography>

				</S.Header>

				{/* Content */}
				<S.Content>
					<Typography tag="h4" weight="bold" style={{ margin: '0px 0px 8px' }}>
						<Trans t={t} i18nKey="commentDiscardConfirmationModal.title">
							Discard Message?
						</Trans>
					</Typography>
					<Typography tag="p">
						<Trans t={t} i18nKey="commentDiscardConfirmationModal.description">
							You have a comment in progress, are you sure you want to discard it?
						</Trans>
					</Typography>
				</S.Content>

				{/* Actions */}
				<S.ActionContainer>
					<Button variant="solid" variation="secondary" onClick={handleCancel}>
						<Trans t={t} i18nKey="actions.cancel">
							Cancel
						</Trans>
					</Button>
					<Button variant="solid" variation="warning" onClick={handleConfirm}>
						<Trans t={t} i18nKey="actions.discard">
							Discard
						</Trans>
					</Button>
				</S.ActionContainer>

			</S.ModalInner>
		</Modal>
	);
});


/**
 * Configuration
 */

CommentDiscardConfirmationModal.displayName = 'CommentDiscardConfirmationModal';
CommentDiscardConfirmationModal.propTypes = {
	className: PropTypes.string,
	isOpen: PropTypes.bool,
	handleCancel: PropTypes.func,
	handleConfirm: PropTypes.func,
};
CommentDiscardConfirmationModal.defaultProps = {
	className: null,
	isOpen: false,
	handleCancel: null,
	handleConfirm: null
};
